import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { useMutation } from '@tanstack/react-query'
import { isEmpty, mapObjIndexed, prop } from 'ramda'
import parsePhoneNumberFromString from 'libphonenumber-js'

import effects from 'app/effects/effects'

import MessageAlert from 'app/components/MessageAlert'

export default function useProfile() {
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()

  const errors = useCallback(
    (text) => {
      const generalError = formatMessage({ id: 'error.InternalServerError' })
      return dispatch(
        MessageAlert.showError({
          message: text ?? generalError,
        }),
      )
    },
    [dispatch, formatMessage],
  )

  const fetchPlaceInfo = useMutation({
    mutationFn: async () => {
      const response = await effects.apis.profileInfo.getPlaceInfo()
      return response.data
    },
    onError: (error) => {
      const msgError = error.response.data
      errors(msgError.details.message)
    },
  })

  const updatePlaceInfo = useMutation({
    mutationFn: async (values) => {
      let countryCallingCode = null
      let nationalNumber = null

      if (!isEmpty(values.phoneNumber)) {
        const parsed = parsePhoneNumberFromString(values.phoneNumber)
        if (parsed && parsed.isValid()) {
          countryCallingCode = parsed.countryCallingCode
          nationalNumber = parsed.nationalNumber
        }
      }
      const obj = {
        ...values,
        categoryId: prop('id', values.category),
        countryCode: countryCallingCode ? `+${countryCallingCode}` : null,
        phoneNumber: nationalNumber ?? null,
      }

      const response = await effects.apis.profileInfo.updatePlaceInfo(
        mapObjIndexed((val, key) => (val === '' ? null : val))(obj),
      )
      return response.data
    },
    onSuccess: () => {
      dispatch(
        MessageAlert.showSuccess({
          message: formatMessage({ id: 'text.change.success' }),
        }),
      )
    },
    onError: (error) => {
      const msgError = error.response.data
      errors(msgError.details.message)
    },
  })

  const resentVerifyEmail = useMutation({
    mutationFn: async () => {
      const response = await effects.apis.resentPlaceVerificationEmail()
      return response.data
    },
    onSuccess: (res, pendingEmail) => {
      dispatch(
        MessageAlert.showSuccess({
          message: formatMessage(
            { id: 'profile.contactDetail.success.email.verification' },
            {
              pendingEmail,
              b: (text) => (
                <span
                  key="profile.contactDetail.success.email.verification"
                  className="font-bold ltr:ml-2 rtl:mr-2"
                >
                  {text}
                </span>
              ),
            },
          ),
        }),
      )
    },
    onError: (error) => {
      const errorCode = error.response?.data?.details?.code
      if (Number(errorCode) === 650) {
        dispatch(
          MessageAlert.showError({
            message: formatMessage({ id: 'error.650.newEmailNotexist' }),
          }),
        )
      } else {
        const msgError = error.response.data
        dispatch(MessageAlert.showNetworkError(formatMessage)(msgError))
      }
    },
  })

  const cancelVerifyEmail = useMutation({
    mutationFn: async () => {
      const response = await effects.apis.cancelPlaceVerificationEmail()
      return response.data
    },
    onSuccess: () => {
      dispatch(
        MessageAlert.showSuccess({
          message: formatMessage({ id: 'text.change.success' }),
        }),
      )
    },
    onError: (error) => {
      const errorCode = error.response?.data?.details?.code
      if (Number(errorCode) === 650) {
        dispatch(
          MessageAlert.showError({
            message: formatMessage({ id: 'error.650.newEmailNotexist' }),
          }),
        )
      } else {
        const msgError = error.response.data
        dispatch(MessageAlert.showNetworkError(formatMessage)(msgError))
      }
    },
  })

  return {
    fetchPlaceInfo,
    updatePlaceInfo,
    resentVerifyEmail,
    cancelVerifyEmail,
  }
}
